import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Row, Space } from 'antd';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { type OptionInput, OptionType } from '../../../../api/types';
import ArrayField from '../../../../components/fields/ArrayField';
import CompanyCurrencyField from '../../../../components/fields/CompanyCurrencyField';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import { useVariantConfigurator } from '../VariantConfiguratorContext';
import { type OptionBlockFormValues, getDefaultOptionTypeInput } from './utils';

type OptionTypeFieldRendererType = {
    prefix: string;
    reference: string;
    disabled?: boolean;
};
export const OptionTypeFieldRenderer = ({ prefix, reference, disabled = false }: OptionTypeFieldRendererType) => {
    const [field] = useField<OptionType>(reference);
    const { t } = useTranslation(['variantConfiguratorDetails']);

    const { variantConfigurator } = useVariantConfigurator();

    switch (field.value) {
        case OptionType.Description:
            return (
                <TranslatedTextAreaField
                    autoSize={{ minRows: 2, maxRows: 3 }}
                    disabled={disabled}
                    label={t('variantConfiguratorDetails:fields.options.description.label')}
                    name={`${prefix}valueText`}
                    required
                    withContentRefinement
                />
            );

        case OptionType.Price:
            return (
                <CompanyCurrencyField
                    {...t('variantConfiguratorDetails:fields.options.price', {
                        returnObjects: true,
                    })}
                    company={variantConfigurator.company}
                    disabled={disabled}
                    name={`${prefix}valuePrice`}
                    required
                />
            );

        default:
            return null;
    }
};

type OptionTypeFieldType = { name: string; disabled?: boolean };

const OptionTypeField = ({ name, disabled = false }: OptionTypeFieldType) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    const { configurator } = useSystemOptions();

    return (
        <ArrayField<OptionInput, OptionBlockFormValues>
            label="Options"
            name={name}
            render={({ remove, push }, options) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    {options?.map((option, index) => (
                        <Card
                            key={option.id ?? index.toString()}
                            extra={
                                !disabled && (
                                    <Button
                                        icon={<DeleteOutlined />}
                                        onClick={() => remove(index)}
                                        type="link"
                                        danger
                                    />
                                )
                            }
                            title={t('variantConfiguratorDetails:sections.options.blockTypeSetting', {
                                number: index + 1,
                            })}
                            type="inner"
                        >
                            <Row gutter={14}>
                                <Col lg={8} xs={24}>
                                    <FormFields.TranslatedInputField
                                        {...t('variantConfiguratorDetails:fields.options.optionName', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}[${index}]label`}
                                        required
                                    />
                                </Col>
                                <Col lg={8} xs={24}>
                                    <FormFields.SelectField
                                        {...t('variantConfiguratorDetails:fields.options.optionType', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}[${index}]type`}
                                        options={configurator.selectOptionTypes}
                                        required
                                        showSearch
                                    />
                                </Col>
                                <Col lg={8} xs={24}>
                                    <OptionTypeFieldRenderer
                                        disabled={disabled}
                                        prefix={`${name}[${index}]`}
                                        reference={`${name}[${index}]type`}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    ))}
                    {!disabled && (
                        <Button icon={<PlusOutlined />} onClick={() => push(getDefaultOptionTypeInput())} type="link">
                            {t('variantConfiguratorDetails:actions.options.addSelectSetting')}
                        </Button>
                    )}
                </Space>
            )}
        />
    );
};

export default OptionTypeField;
